import { useCallback, useEffect, useState } from 'react';

import { useSanitiseHoF } from 'actions/hall-of-fame';

import {
  OTHER_EXAM_KEY,
  SCORE_TYPE_DISPUTED,
  STATUS,
  VALUE_FIELD_FOR_SCORE_TYPE,
  VALUE_FIELD_HINTS,
  VALUE_FIELD_LABELS,
} from '../constants';

const MAX_EXAM_NAME = 120;
// `certificate_rules.SCORE_MAX_CHARS`. A certificate has always refused a longer result,
// and `_issue_certificate` swallows that refusal as an INFO log — so the older, looser 80
// did not buy a longer result, it bought one whose certificate silently never arrives.
const MAX_RESULT = 48;

const submissionOf = row => (row && row.submission) || {};

const isCustom = row => submissionOf(row).examKey === OTHER_EXAM_KEY;

// The same gate `status_rules.frozen_reason` enforces on the server, so the drawer cannot
// offer an edit the endpoint will reject.
const notFrozen = row =>
  !!row && row.status !== STATUS.PUBLISHED && row.status !== STATUS.REMOVED;

// The name stays custom-only: a listed exam's name belongs to the library, so correcting it
// here would put one row out of step with every other row on the same exam. The API refuses
// it for the same reason, and this mirrors that rather than guessing at it.
const canEditName = row => isCustom(row) && notFrozen(row);

const lower = value =>
  String(value || '')
    .trim()
    .toLowerCase();

// What the moderator supplies for a row that cannot assemble itself: the tutor's one typed
// sentence split apart, and the scoring type the tutor and the library disagree about.
//
// That disagreement is every parked row on prod — 21 of 21 — and nothing could write it.
// Settling it is not enough on its own: `normalise_value` reads a different typed field per
// scoring type and falls back to `score` when it is blank, so agreeing that an exam is
// letter-graded while the mark stays in `score` publishes "achieved 99" on a letter ladder.
// The value comes with the verdict.
const useRecordSupply = ({ row, libraryScoreType, refresh, reportOutcome }) => {
  const [examName, setExamName] = useState('');
  const [result, setResult] = useState('');
  const [settling, setSettling] = useState(false);
  const [value, setValue] = useState('');

  const rowId = row && row.id;
  const submission = submissionOf(row);
  const nameEditable = canEditName(row);

  const disputed = ((row && row.blockers) || []).some(
    b => b && b.kind === 'broken' && b.code === SCORE_TYPE_DISPUTED,
  );
  const declaredType = lower(submission.scoreType);
  const settledType = lower(libraryScoreType);
  // Which input the settled type will actually be read from. `score` is the fallback the
  // service itself uses when a type names no field of its own.
  const valueField = VALUE_FIELD_FOR_SCORE_TYPE[settledType] || 'score';
  // Nothing to move when the settled type reads the same field the value is already in.
  const valueMoves = valueField !== 'score';

  // Seeded from the submission, and reset when the drawer opens on a different record so
  // one row's correction is never carried onto the next.
  useEffect(() => {
    const fresh = submissionOf(row);
    setExamName(fresh.otherExamName || '');
    setResult(fresh.score || '');
    setSettling(false);
  }, [rowId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Prefilled from the mark the tutor did send: the value is right and only the field it
  // sits in is wrong, so retyping it would be busywork. Keyed on the field as well as the
  // row because the library that names it is fetched — resolving after the drawer is open
  // re-seeds from the right field rather than leaving the `score` fallback. It cannot
  // clobber typing: the input appears only once settling is ticked.
  useEffect(() => {
    setValue(submissionOf(row)[valueField] || '');
  }, [rowId, valueField]); // eslint-disable-line react-hooks/exhaustive-deps

  const { post: postSanitise, isProcessing: saving } = useSanitiseHoF({
    onComplete: reportOutcome('Correction', refresh),
  });

  const trimmedName = examName.trim();
  const trimmedResult = result.trim();
  const trimmedValue = value.trim();

  // On a listed exam the name is not the moderator's, so it is neither compared nor
  // required — without this the result alone could never be dirty, nor ever be valid.
  const textDirty =
    (nameEditable && trimmedName !== (submission.otherExamName || '')) ||
    trimmedResult !== (submission.score || '');
  const isDirty = textDirty || settling;

  // `maxLength` stops a moderator typing past the cap, but not a value that arrived over
  // it — and the tutor's one sentence lands in both fields, so most of these rows open
  // over one cap or the other, with Save disabled and nothing saying why.
  const resultTooLong = trimmedResult.length > MAX_RESULT;
  const nameTooLong = nameEditable && trimmedName.length > MAX_EXAM_NAME;

  const textValid =
    !!trimmedResult &&
    !resultTooLong &&
    (!nameEditable || (!!trimmedName && !nameTooLong));
  // Settling without the value the settled type reads is the failure mode this whole
  // control exists to prevent, so it is not a valid submission.
  const settleValid =
    !settling ||
    (!valueMoves ? true : !!trimmedValue && trimmedValue.length <= MAX_RESULT);
  const isValid = textValid && settleValid;

  const save = useCallback(() => {
    if (!isDirty || !isValid) return;
    // Only what this row lets a moderator own: sending `examName` on a listed exam is a
    // 400 from the rule, not a silent no-op.
    const body = nameEditable
      ? { examName: trimmedName, result: trimmedResult }
      : { result: trimmedResult };
    if (settling) {
      body.scoreType = settledType;
      // `result` already carries `score`, so only a type reading some other field adds one.
      if (valueMoves) body[valueField] = trimmedValue;
    }
    postSanitise(body, rowId);
  }, [
    isDirty,
    isValid,
    nameEditable,
    trimmedName,
    trimmedResult,
    settling,
    settledType,
    valueMoves,
    valueField,
    trimmedValue,
    postSanitise,
    rowId,
  ]);

  return {
    examName,
    setExamName,
    result,
    setResult,
    // Editable on any achievement, not just a custom one: prod held six parked rows with a
    // mistyped result — 2525 for what is plainly 25/25, 20-20, 39-40 — and every one was a
    // listed exam, so a custom-only gate left nobody able to fix any of them.
    canEdit: notFrozen(row),
    canEditName: nameEditable,
    isDirty,
    isValid,
    saving,
    save,
    maxExamName: MAX_EXAM_NAME,
    maxResult: MAX_RESULT,
    resultTooLong,
    resultLength: trimmedResult.length,
    nameTooLong,
    nameLength: trimmedName.length,
    // The disagreement, and the settlement on offer.
    disputed: disputed && !!settledType && notFrozen(row),
    declaredType,
    settledType,
    settling,
    setSettling,
    valueMoves,
    value,
    setValue,
    valueLabel: VALUE_FIELD_LABELS[valueField] || 'Result',
    valueHint: VALUE_FIELD_HINTS[valueField],
  };
};

export default useRecordSupply;
